import { useEffect, useRef, useState, useCallback } from "react";
import { Terminal as TermIcon, X, Loader } from "lucide-react";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { startServerSshSession } from "../api";

// =============================================================
//  ServerTerminal — in-browser SSH terminal (Server RMM, Linux)
// -------------------------------------------------------------
//  Mints a single-use session token, opens the brokered /terminal
//  WebSocket, and wires xterm.js to it:
//    keystrokes → BINARY frames  (backend pipes them into the PTY)
//    resize     → TEXT JSON frame {type:"resize",cols,rows}
//    PTY output ← BINARY frames  → term.write
//  The session is brokered entirely outbound through the server's
//  agent (serverRmmTunnel.js) — no inbound port on the box.
// =============================================================

const WS_BASE = (process.env.REACT_APP_API_URL || window.location.origin).replace(/^http/, "ws");

export default function ServerTerminal({ serverId, serverName, sshUser, onClose }) {
  const hostRef   = useRef(null);
  const termRef   = useRef(null);
  const fitRef    = useRef(null);
  const wsRef     = useRef(null);
  const [status, setStatus] = useState("connecting"); // connecting | open | closed | error
  const [detail, setDetail] = useState("");

  // ── draggable window position ──
  const [pos, setPos] = useState(() => ({
    x: Math.max(12, window.innerWidth  - 920 - 24),
    y: Math.max(12, window.innerHeight - 580 - 24),
  }));
  const dragRef = useRef(null);
  const onDragStart = (e) => {
    if (e.button !== 0) return;
    dragRef.current = { ox: e.clientX - pos.x, oy: e.clientY - pos.y };
    e.preventDefault();
  };
  useEffect(() => {
    const move = (e) => {
      if (!dragRef.current) return;
      setPos({
        x: Math.min(window.innerWidth - 120, Math.max(0, e.clientX - dragRef.current.ox)),
        y: Math.min(window.innerHeight - 60, Math.max(0, e.clientY - dragRef.current.oy)),
      });
    };
    const up = () => { dragRef.current = null; };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  }, []);

  const cleanup = useCallback(() => {
    try { wsRef.current?.close(); } catch {}
    try { termRef.current?.dispose(); } catch {}
    wsRef.current = null;
    termRef.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    const term = new Terminal({
      cursorBlink: true,
      fontSize: 13,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      theme: { background: "#0a0f1c", foreground: "#cdd9ef", cursor: "#4f6fff" },
      scrollback: 5000,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    termRef.current = term;
    fitRef.current = fit;
    term.open(hostRef.current);
    try { fit.fit(); } catch {}

    (async () => {
      let token;
      try {
        const r = await startServerSshSession(serverId);
        token = r.data.token;
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setDetail(e.response?.data?.error || "Failed to start the terminal session.");
        return;
      }
      if (cancelled) return;

      const cols = term.cols || 80;
      const rows = term.rows || 24;
      const url = `${WS_BASE}/api/server-rmm/terminal?token=${encodeURIComponent(token)}&cols=${cols}&rows=${rows}`;
      const ws = new WebSocket(url);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        setStatus("open");
        term.focus();
      };
      ws.onmessage = (ev) => {
        if (typeof ev.data === "string") term.write(ev.data);
        else term.write(new Uint8Array(ev.data));
      };
      ws.onerror = () => {
        if (!cancelled && status !== "closed") { setStatus("error"); setDetail("Connection error."); }
      };
      ws.onclose = (ev) => {
        if (cancelled) return;
        setStatus("closed");
        if (ev.reason) setDetail(ev.reason);
      };

      // keystrokes → binary frames (so the broker writes them to the PTY)
      const enc = new TextEncoder();
      term.onData((d) => { if (ws.readyState === WebSocket.OPEN) ws.send(enc.encode(d)); });
      // resize → JSON control frame
      term.onResize(({ cols, rows }) => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "resize", cols, rows }));
      });
    })();

    const onWinResize = () => { try { fitRef.current?.fit(); } catch {} };
    window.addEventListener("resize", onWinResize);

    return () => { cancelled = true; window.removeEventListener("resize", onWinResize); cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverId]);

  // refit shortly after mount / position settle so xterm matches the body box
  useEffect(() => { const t = setTimeout(() => { try { fitRef.current?.fit(); } catch {} }, 80); return () => clearTimeout(t); }, []);

  const statusDot = { connecting: "#fbbf24", open: "#34d399", closed: "#5d6f93", error: "#f87171" }[status];
  const statusText = {
    connecting: "Connecting…", open: "Connected", closed: "Session ended", error: "Error",
  }[status];

  return (
    <div
      style={{
        position: "fixed", left: pos.x, top: pos.y, width: 920, height: 580, zIndex: 9998,
        background: "#0a0f1c", border: "1px solid rgba(79,111,255,0.35)", borderRadius: 12,
        boxShadow: "0 18px 60px rgba(2,6,16,0.6), 0 0 30px rgba(79,111,255,0.12)",
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}
    >
      {/* header */}
      <div
        onMouseDown={onDragStart}
        style={{
          height: 40, flexShrink: 0, display: "flex", alignItems: "center", gap: 10, padding: "0 12px",
          background: "#0f172a", borderBottom: "1px solid rgba(79,111,255,0.18)", cursor: "move", userSelect: "none",
        }}
      >
        <TermIcon size={15} color="#22d3ee" />
        <div style={{ fontSize: 13, fontWeight: 700, color: "#cdd9ef", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {serverName || "Server"} <span style={{ color: "#5d6f93", fontWeight: 500 }}>· {sshUser ? sshUser : "root"}@ssh</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto", fontSize: 11, color: "#8a9cc2" }}>
          {status === "connecting" && <Loader size={12} style={{ animation: "spin 1s linear infinite" }} />}
          <span style={{ width: 7, height: 7, borderRadius: 99, background: statusDot, display: "inline-block" }} />
          <span style={{ color: "#7e8db0" }}>{statusText}</span>
        </div>
        <button
          onClick={onClose}
          title="Close session"
          style={{ background: "none", border: "none", color: "#7e8db0", cursor: "pointer", display: "flex", padding: 4, borderRadius: 6 }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(248,113,113,0.15)"; e.currentTarget.style.color = "#fca5a5"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#7e8db0"; }}
        >
          <X size={16} />
        </button>
      </div>

      {/* terminal body */}
      <div style={{ position: "relative", flex: 1, minHeight: 0, padding: 8, background: "#0a0f1c" }}>
        <div ref={hostRef} style={{ width: "100%", height: "100%" }} />
        {(status === "error" || status === "closed") && (
          <div style={{
            position: "absolute", inset: 8, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            gap: 8, background: "rgba(10,15,28,0.82)", color: status === "error" ? "#fca5a5" : "#9fb0d0", textAlign: "center", padding: 24,
          }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{status === "error" ? "Could not open terminal" : "Session ended"}</div>
            {detail && <div style={{ fontSize: 12, maxWidth: 460, lineHeight: 1.5 }}>{detail}</div>}
            <button onClick={onClose} style={{ marginTop: 8, padding: "7px 16px", borderRadius: 8, border: "1px solid rgba(79,111,255,0.3)", background: "rgba(79,111,255,0.12)", color: "#cdd9ef", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              Close
            </button>
          </div>
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
